import Bookmark from "@untitled-ui/icons-react/build/esm/Bookmark";
import FilterLines from "@untitled-ui/icons-react/build/esm/FilterLines";
import classnames from "classnames";
import { useCallback, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";
import { saveSearch } from "../../../../services/post-save-search";
import {
  addError,
  showFilterModal,
} from "../../../../store/reducer/_uiReducer";
import {
  getAppliedFilters,
  getCurrentModelTypeAppliedFilters,
  getCurrentModelTypeFilters,
  getCurrentSearchPeriod,
  getQueryValue,
  getSearchModelType,
  getSearchResultsType,
  getSearchType,
  getSelectedPersonFilter,
} from "../../../../store/selectors";
import { notify } from "../../../../utils/_notify";
import { ErrorMessage } from "../../../../utils/classes";
import { PDLButton } from "../../../components/dna";
import { Logo, OmniBar, UserDetails } from "../../global";
import "./index.scss";

function Header({ headerClass = null }) {
  const dispatch = useDispatch();

  const searchType = useSelector(getSearchType);
  const appliedFilters = useSelector(getAppliedFilters);
  const currentPeriod = useSelector(getCurrentSearchPeriod);
  const selectedPersonFilter = useSelector(getSelectedPersonFilter);
  const appliedModelType = useSelector(getSearchModelType);
  const currentQueryValue = useSelector(getQueryValue);
  const searchResultsType = useSelector(getSearchResultsType);
  const currentModelTypeFilters = useSelector(getCurrentModelTypeFilters);
  const modalTypeAppliedFilters = useSelector(
    getCurrentModelTypeAppliedFilters,
  );
  const [isSavingSearch, setIsSavingSearch] = useState(false);

  const currentSearchType = searchType.id;
  const currentAppliedFilterValues =
    appliedFilters[currentSearchType]?.flatMap((filter) => filter.values) ?? [];

  const modalTypeFilterCount =
    currentSearchType !== "modelos" || appliedModelType === "all"
      ? 0
      : modalTypeAppliedFilters.length + 1;

  const filterCount =
    currentAppliedFilterValues.length +
    (currentPeriod?.period && currentPeriod?.period !== "always" ? 1 : 0) +
    (selectedPersonFilter?.length > 2 ? 1 : 0) +
    modalTypeFilterCount;

  const toggleFilterModal = useCallback(() => {
    dispatch(showFilterModal());
  }, [dispatch]);

  const handleSaveSearch = useCallback(async () => {
    if (!currentQueryValue || currentQueryValue.trim().length === 0) {
      const errorMessage = new ErrorMessage({
        message: "Nenhuma pesquisa ativa para salvar.",
        title: "Pesquisa Inválida",
        variant: "warning",
      });
      dispatch(addError(errorMessage));
      return;
    }

    setIsSavingSearch(true);

    try {
      const collectionType =
        searchType?.collectionType || searchResultsType || "Jurisprudencia";

      const filters = {};
      const currentSearchTypeId = searchType?.id;

      if (currentSearchTypeId === "modelos") {
        if (appliedModelType && appliedModelType !== "all") {
          filters.tipoModelo = appliedModelType;
        }

        const appliedValues = modalTypeAppliedFilters || [];
        const modelFilters = currentModelTypeFilters || [];

        modelFilters.forEach(({ id, options = [] }) => {
          const selected = options
            .filter((opt) => opt.checked && appliedValues.includes(opt.label))
            .map((opt) => opt.label);

          if (selected.length) {
            filters[id] = selected.join(",");
          }
        });
      }

      const appliedValues =
        appliedFilters?.[currentSearchTypeId || "all"] || [];
      appliedValues.forEach(({ id, values = [] }) => {
        if (id && values.length) {
          filters[id] = values.join(",");
        }
      });

      const { period, startDate, endDate } = currentPeriod || {};

      if (searchType?.personFilter?.param && selectedPersonFilter) {
        filters[searchType.personFilter.param] = selectedPersonFilter;
      }

      if (period && period !== "always") {
        if (startDate) filters.dataInicio = startDate;
        if (endDate) filters.dataFim = endDate;
      }

      const params = {
        q: currentQueryValue,
        limit: 10,
        page: 1,
        sort: "relevancia",
        collectionType,
        ...filters,
      };

      const searchParams = new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      );

      const url = `/api/lex/produto/v1/pesquisa/${collectionType.toLowerCase()}?${searchParams.toString()}`;

      const name = currentQueryValue;

      await saveSearch({
        nome: name,
        tipo: collectionType,
        parametros: params,
        url,
      });

      notify(dispatch, {
        message: "Pesquisa salva com sucesso!",
        title: "Sucesso",
        variant: "success",
      });
    } catch (error) {
      const apiMessage =
        error?.response?.data?.erro ||
        error?.response?.data?.message ||
        error?.message;
      const errorMessage = new ErrorMessage({
        message: apiMessage || "Erro ao salvar pesquisa",
        title: "Erro",
        variant: "warning",
      });
      dispatch(addError(errorMessage));
    } finally {
      setIsSavingSearch(false);
    }
  }, [
    appliedFilters,
    appliedModelType,
    currentModelTypeFilters,
    currentPeriod,
    currentQueryValue,
    dispatch,
    modalTypeAppliedFilters,
    searchResultsType,
    searchType,
    selectedPersonFilter,
  ]);

  return (
    <header className={classnames("pdl_component_header", headerClass)}>
      <Container>
        <Row className="d-none d-md-flex">
          <Col className="d-flex flex-row align-items-center justify-contents-between px-0 py-3">
            <Logo variant={headerClass === "home" ? "header_home" : "header"} />
            <OmniBar headerClass={headerClass} />
            <UserDetails
              triggerVariant={headerClass === "home" ? "white" : "default"}
            />
          </Col>
        </Row>

        <div className="d-md-none header-mobile">
          <Row className="align-items-center justify-content-between py-2">
            <Col xs="auto" className="ps-0">
              <Logo
                variant={headerClass === "home" ? "header_home" : "header"}
              />
            </Col>
            <Col xs="auto" className="pe-0">
              <UserDetails
                triggerVariant={headerClass === "home" ? "white" : "default"}
              />
            </Col>
          </Row>

          <Row className="align-items-center py-2">
            <Col xs={9} className="ps-0">
              <OmniBar headerClass={headerClass} isMobile={true} />
            </Col>
            <Col
              xs={3}
              className="pe-0 d-flex justify-content-end header-mobile-buttons"
            >
              {headerClass !== "home" && (
                <>
                  <PDLButton
                    variant="primary"
                    size="sm"
                    className="px-2"
                    disabled={searchType.id === "all"}
                    onClick={toggleFilterModal}
                  >
                    <FilterLines size={16} />
                    {filterCount > 0 && (
                      <p className="count-box">{filterCount}</p>
                    )}
                  </PDLButton>
                  <PDLButton
                    variant="secondary"
                    size="sm"
                    className="px-2"
                    onClick={handleSaveSearch}
                    disabled={
                      isSavingSearch ||
                      !currentQueryValue ||
                      currentQueryValue.trim().length === 0
                    }
                  >
                    <Bookmark size={16} />
                  </PDLButton>
                </>
              )}
            </Col>
          </Row>
        </div>
      </Container>
    </header>
  );
}
export default Header;
